/**
 * The React boundary (architecture §8): read-only engine services plus
 * `dispatch`, provided to every editor panel.
 *
 * Panels never import world or command internals. They read through the store
 * hooks (editor/hooks) and write only by dispatching JSON command requests,
 * alone or several as one undo step (`dispatchAll`).
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { ContentIndex, ContentSeed } from "../assets/content";
import type { CommandBus } from "../commands/CommandBus";
import type { AsyncCommandResult, CommandRequest, CommandResult } from "../commands/types";
import type { FrameLoop } from "../core/FrameLoop";
import type { Log } from "../core/Log";
import type { ReadableStore } from "../core/store";
import type { RenderBackend } from "../render/RenderBackend";
import { formatRoute } from "./router/route";
import { THEMES } from "../themes";
import type { Theme } from "../themes";
import type { EntityId } from "../world/entity";
import type { ComponentRegistry } from "../world/schema/ComponentRegistry";
import type { WorldReader } from "../world/World";
import type { LevelService } from "./levels/LevelService";
import type { RenderBridge } from "./render-bridge/RenderBridge";
import type { Selection } from "./selection/Selection";
import type { EditorSettings } from "./settings/EditorSettings";
import type { PanelFocus } from "./shell/PanelFocus";
import type { TourState } from "./tour/TourService";
import type { ViewportCamera } from "./viewport/ViewportCamera";
import type { EntityClipboard } from "./clipboard/EntityClipboard";
import type { PlacementPreview } from "./viewport/PlacementPreview";
import type { CameraPreview } from "./viewport/cameraPreview";
import type { PlayState } from "./play/PlaySession";
import { isGroup } from "./selection/selectionEdits";
import type { Edit } from "./selection/selectionEdits";

export type Dispatch = (request: CommandRequest) => CommandResult | Promise<AsyncCommandResult>;

/**
 * Runs synchronous requests as one transaction (one undo step, one journal
 * entry) and returns each one's result, in order.
 */
export type DispatchAll = (label: string, requests: readonly CommandRequest[]) => CommandResult[];

/** Profile links resolved for this deployment (the Pages base path applied). */
export interface EditorLinks {
  readonly resume: string;
  readonly github: string | null;
  readonly linkedin: string | null;
  /** A level's address, under the deployment base (the Router's grammar). */
  readonly level: (id: string) => string;
}

export interface EditorServices {
  /** The system asks for reduced motion (`prefers-reduced-motion`), which Realtime's Automatic follows. */
  readonly reducedMotion: boolean;
  readonly world: WorldReader;
  readonly components: ComponentRegistry;
  readonly selection: Selection;
  readonly settings: EditorSettings;
  readonly levels: LevelService;
  readonly camera: ViewportCamera;
  readonly bridge: RenderBridge;
  readonly panelFocus: PanelFocus;
  readonly tour: ReadableStore<TourState>;
  /** Levels with edits saved in this browser (modified marks, Reset to Default). */
  readonly savedLevels: ReadableStore<ReadonlySet<string>>;
  /** Where copied entities go (the system clipboard, with an in-editor fallback). */
  readonly clipboard: EntityClipboard;
  /** The ghost of an asset dragged over the viewport. */
  readonly placement: PlacementPreview;
  /** What the selected camera sees, drawn in the viewport's corner; null without one. */
  readonly cameraPreview: ReadableStore<CameraPreview | null>;
  /** The camera the viewport is looking through and moving (Pilot), or null. */
  readonly pilot: ReadableStore<EntityId | null>;
  /** Play in Editor: what's playing (null when nothing is), and whether the level can play. */
  readonly play: ReadableStore<PlayState | null> & { modeOfLevel(): string | null };
  readonly log: Log;
  readonly bus: Pick<CommandBus, "undoState" | "journal" | "openSession">;
  readonly content: {
    readonly index: ContentIndex;
    readonly seed: ContentSeed;
    /** Absolute URL of a content-relative path (thumbnails, downloads), base path applied. */
    url(path: string): string;
  };
  readonly render: { readonly backend: RenderBackend; readonly frameLoop: FrameLoop };
  readonly links: EditorLinks;
  /** The active theme (switched with `theme.set`) and every registered theme. */
  readonly theme: {
    readonly current: ReadableStore<Theme>;
    /** `system` (follow the OS) or a theme id: what the visitor chose, not what it resolved to. */
    readonly choice: ReadableStore<string>;
    readonly all: readonly Theme[];
  };
  readonly dispatch: Dispatch;
  readonly dispatchAll: DispatchAll;
}

/** The module services an EditorServices is assembled from (the app's engine, or a test engine). */
export interface EditorServiceSources {
  readonly core: { readonly log: Log; readonly frameLoop: FrameLoop };
  readonly commands: { readonly bus: CommandBus };
  readonly assets: {
    readonly index: ContentIndex;
    readonly seed: ContentSeed;
    readonly registry: { url(path: string): string };
  };
  readonly renderer: { readonly backend: RenderBackend };
  readonly world: { readonly world: WorldReader; readonly components: ComponentRegistry };
  readonly editor: {
    readonly selection: Selection;
    readonly settings: EditorSettings;
    readonly levels: LevelService;
    readonly camera: ViewportCamera;
    readonly bridge: RenderBridge;
    readonly panelFocus: PanelFocus;
    readonly tour: ReadableStore<TourState>;
    readonly saves: { readonly savedLevels: ReadableStore<ReadonlySet<string>> };
    readonly clipboard: EntityClipboard;
    readonly placement: PlacementPreview;
    readonly cameraPreview: ReadableStore<CameraPreview | null>;
    readonly pilot: ReadableStore<EntityId | null>;
    readonly play: ReadableStore<PlayState | null> & { modeOfLevel(): string | null };
    readonly reducedMotion: boolean;
  };
  readonly theme: {
    readonly theme: { readonly current: ReadableStore<Theme> };
    readonly choice: ReadableStore<string>;
  };
  /** The Pages base path, e.g. "/" or "/portfolio/". */
  readonly base: string;
}

export function editorServicesFrom(sources: EditorServiceSources): EditorServices {
  const { bus } = sources.commands;
  const { profile } = sources.assets.seed;

  return {
    ...sources.editor,
    savedLevels: sources.editor.saves.savedLevels,
    world: sources.world.world,
    components: sources.world.components,
    log: sources.core.log,
    bus,
    content: {
      index: sources.assets.index,
      seed: sources.assets.seed,
      url: (path) => sources.assets.registry.url(path),
    },
    render: { backend: sources.renderer.backend, frameLoop: sources.core.frameLoop },
    links: {
      // The seed stores a site-absolute path; deployments live under a base path (A-04).
      resume: `${sources.base}${profile.resume.replace(/^\/+/, "")}`,
      github: profile.links.github ?? null,
      linkedin: profile.links.linkedin ?? null,
      level: (id) => formatRoute({ levelId: id }, sources.base),
    },
    theme: { current: sources.theme.theme.current, choice: sources.theme.choice, all: THEMES },
    // One entry point for panels: async commands (level.open) route to executeAsync.
    dispatch: (request) =>
      bus.isAsync(request.type) ? bus.executeAsync(request) : bus.execute(request),
    dispatchAll: (label, requests) =>
      bus.transaction(label, () => requests.map((request) => bus.execute(request))),
  };
}

/** Runs an edit: a lone request as it is, a group as one transaction. Nothing for null. */
export function runEdit(
  { dispatch, dispatchAll }: Pick<EditorServices, "dispatch" | "dispatchAll">,
  edit: Edit | null,
): void {
  if (!edit) return;
  if (isGroup(edit)) dispatchAll(edit.label, edit.requests);
  else void dispatch(edit);
}

const EditorContext = createContext<EditorServices | null>(null);

export function EditorProvider({
  services,
  children,
}: {
  services: EditorServices;
  children: ReactNode;
}) {
  return <EditorContext.Provider value={services}>{children}</EditorContext.Provider>;
}

export function useEditor(): EditorServices {
  const services = useContext(EditorContext);
  if (!services) throw new Error("useEditor must be used inside <EditorProvider>");
  return services;
}
